import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, Label } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { getArticle, ARTICLES } from "../data/articles";

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return null;

  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);
  const dateFmt = new Date(article.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.metaDescription,
    datePublished: article.date,
    author: { "@type": "Organization", name: "Resineko" },
  };

  return (
    <>
      <Seo title={article.metaTitle} description={article.metaDescription} path={`/blog/${article.slug}`} jsonLd={jsonLd} />

      <article className="bg-[#0A0A0A]" data-testid="article-page">
        <header className="relative overflow-hidden pb-16 pt-40 md:pb-24 md:pt-52">
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{ background: "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(4,120,87,0.15), transparent)" }}
          />
          <div className="relative mx-auto max-w-3xl px-5 md:px-10">
            <Link to="/blog" data-testid="article-back" className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#8C8C8C] transition-colors hover:text-[#2FBF8F]">
              <ArrowLeft size={13} className="transition-transform duration-300 group-hover:-translate-x-1" /> Tous les articles
            </Link>
            <p className="mt-8"><Label>{article.category} — {dateFmt} — {article.readTime} de lecture</Label></p>
            <h1 className="mt-6 font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl" data-testid="article-h1">
              {article.title}
            </h1>
          </div>
        </header>

        <Reveal>
          <div className="mx-auto max-w-5xl px-5 md:px-10">
            <img src={article.image} alt={article.title} className="aspect-[21/9] w-full border border-white/10 object-cover" />
          </div>
        </Reveal>

        <div className="mx-auto max-w-3xl px-5 py-16 md:px-10 md:py-24">
          {article.content.map((block, i) => {
            if (block.type === "h2") {
              return (
                <Reveal key={i}>
                  <h2 className="mt-12 font-serif text-3xl font-light text-[#F4F4F0]">{block.text}</h2>
                </Reveal>
              );
            }
            if (block.type === "list") {
              return (
                <Reveal key={i}>
                  <ul className="mt-6 space-y-4">
                    {block.items.map((item, j) => (
                      <li key={j} className="flex gap-4 text-sm leading-relaxed text-[#8C8C8C] md:text-base">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#047857]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            }
            return (
              <Reveal key={i}>
                <p className="mt-6 text-sm leading-relaxed text-[#8C8C8C] md:text-base">{block.text}</p>
              </Reveal>
            );
          })}

          <Reveal>
            <div className="mt-16 border border-[#047857]/30 bg-[#047857]/5 p-8">
              <h2 className="font-serif text-2xl font-light text-[#F4F4F0]">Un projet en tête ?</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#8C8C8C]">
                Décrivez-nous votre projet : Resineko vous oriente vers la solution la plus adaptée et vous établit un devis personnalisé.
              </p>
              <Link
                to="/devis"
                data-testid="article-devis-cta"
                className="group relative mt-6 inline-flex overflow-hidden bg-[#047857] px-7 py-3.5 text-xs font-medium uppercase tracking-[0.2em] text-white"
              >
                <span className="absolute inset-0 -translate-x-full bg-[#065F46] transition-transform duration-500 ease-out group-hover:translate-x-0" />
                <span className="relative flex items-center gap-2.5">Demander un devis <ArrowRight size={14} /></span>
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="border-t border-white/10 bg-[#0e0e0e] py-16">
          <div className="mx-auto max-w-5xl px-5 md:px-10">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#8C8C8C]">À lire ensuite</p>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {others.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`article-next-${a.slug}`} className="group border border-white/10 bg-[#111] p-7 transition-colors duration-300 hover:border-[#047857]/50">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#2FBF8F]">{a.category}</p>
                  <h3 className="mt-3 font-serif text-xl font-light text-[#F4F4F0] transition-colors group-hover:text-[#2FBF8F]">{a.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <CTASection />
    </>
  );
};

export default ArticlePage;
